import { ReactNode } from 'react';
import { ArrowRight2, Clock, IconComponent, Warning2 } from '@/components/icons';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { PaidEntitlementPlan } from '@/api/entitlements';

type AvailablePlan = {
    id: PaidEntitlementPlan;
    name: string;
};

/**
 * The vocabulary the AI panel's body is written in.
 *
 * The body answers one question — why can't I press the button — and it may
 * answer in one of three grammars: something you do (a `GateAction`), something
 * you wait for (a `GateNotice`), or something that is already there and you may
 * still want to change (also a `GateAction`). There is no fourth. Everything
 * the old checklist said beyond those — who owes it, whether it is optional,
 * whether a sibling covers it, that a satisfied row is satisfied — was state
 * the reader had to decode before knowing what to do.
 *
 * Kept together in one file, and used by both `AIDataSources` and
 * `AIGeneratorCard`, so the credits gate and the source gate cannot drift into
 * looking like two different ideas.
 */

/**
 * How many things are in the way, as the panel's opening line.
 *
 * The count rather than the names: the names are right below it, each on its
 * own button, and repeating them here would say everything twice. What the
 * count adds is the size of the gap, which is the one thing a stack of buttons
 * does not tell you at a glance.
 */
export function GateCount({ count }: { count: number }) {
    return (
        <p className="text-[15px] font-semibold leading-tight tracking-[-0.01em] text-foreground">
            {count === 1 ? 'Falta 1 item para gerar' : `Faltam ${count} itens para gerar`}
        </p>
    );
}

export type GateActionProps = {
    label: string;
    onClick: () => void;
    /** Leading glyph, when the action is worth marking (adding a document). */
    icon?: IconComponent;
    disabled?: boolean;
    disabledTooltip?: string;
    unavailablePlan?: AvailablePlan | null;
    /** Id of the element explaining the state this action clears. */
    describedBy?: string;
    id?: string;
};

/**
 * One thing you can do, as a full-width target.
 *
 * The label carries the verb and the noun ("Adicionar anamnese"), so the button
 * is also the sentence — which is why nothing is written above it. A link would
 * have needed a row of explanation beside it to say the same thing.
 */
export function GateAction({
    label,
    onClick,
    icon: Icon,
    disabled = false,
    disabledTooltip,
    unavailablePlan,
    describedBy,
    id,
}: GateActionProps) {
    const reasonId = id ? `${id}-reason` : undefined;

    const button = (
        <Button
            id={id}
            type="button"
            variant="outline"
            // `aria-disabled` rather than the native attribute: a natively
            // disabled button leaves the a11y tree and takes its own reason
            // with it, and the reason is the whole point of a blocked route.
            aria-disabled={disabled || undefined}
            aria-describedby={disabled ? reasonId : describedBy}
            onClick={(event) => {
                if (disabled) {
                    event.preventDefault();
                    return;
                }
                onClick();
            }}
            className={cn(
                'h-auto w-full justify-start gap-2.5 rounded-xl border-border-strong px-3 py-2.5 text-[13px] font-semibold',
                disabled && 'cursor-not-allowed text-muted-foreground',
            )}
        >
            {Icon ? <Icon className="size-[15px] shrink-0 text-primary" /> : null}
            <span className="min-w-0 flex-1 text-left [overflow-wrap:anywhere]">{label}</span>
            {unavailablePlan ? (
                <FeatureUnavailableChip availablePlan={unavailablePlan} side="left" />
            ) : null}
            <ArrowRight2 className="size-3.5 shrink-0 text-muted-foreground" />
            {disabled && disabledTooltip ? (
                <span id={reasonId} className="sr-only">
                    {disabledTooltip}
                </span>
            ) : null}
        </Button>
    );

    if (!disabled || !disabledTooltip) return button;

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="left">{disabledTooltip}</TooltipContent>
        </Tooltip>
    );
}

/**
 * Something happening that you cannot act on, as one sentence.
 *
 * `tone="failure"` is the only colour left in the body. Everything else that
 * used to be amber or blue was a to-do, and a to-do is a button now — so the
 * one place colour still means something is the place where something broke.
 */
export function GateNotice({
    tone = 'waiting',
    children,
    id,
}: {
    tone?: 'waiting' | 'failure';
    children: ReactNode;
    id?: string;
}) {
    const Icon = tone === 'failure' ? Warning2 : Clock;

    return (
        <p
            id={id}
            className={cn(
                'flex items-start gap-2 text-[13px] leading-snug',
                tone === 'failure' ? 'text-destructive-strong' : 'text-muted-foreground',
            )}
        >
            <Icon className="mt-px size-[15px] shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1">{children}</span>
        </p>
    );
}
